// src/app/quotaEngine.ts
/**
 * 存储配额与物理占用计算引擎
 */

/**
 * 规范化 rootPath 格式，确保空为 ""，非空为 "prefix/"
 */
export function normalizeRootPath(path?: string | null): string {
  if (!path) return "";
  const clean = path.replace(/^\/+/, "").replace(/\/+$/, "");
  return clean ? clean + "/" : "";
}

export interface QuotaUserItem {
  username?: string;
  rootPath: string;
  usedBytes: number;
}

/**
 * 计算存储桶真实物理总占用（层级去重算法）
 * 
 * 核心原理：
 * 1. 若存在根目录为全桶 (rootPath === "") 的用户（如管理员），该用户已用容量已包含整个桶内所有文件（含所有子目录），直接作为桶总占用。
 * 2. 若无全桶用户，则按目录层级从浅到深排序，排除所有已被更顶层目录包含的子目录，并对相同根目录去重，避免重复统计。
 */
export function calculatePhysicalTotalUsedBytes(users: QuotaUserItem[]): number {
  if (!users || users.length === 0) return 0;

  // 1. 先按规范化后的 rootPath 分组，取每个路径下的最大 usedBytes（避免同目录多账号重复累加）
  const pathMap = new Map<string, number>();
  for (const u of users) {
    const norm = normalizeRootPath(u.rootPath);
    const bytes = Number(u.usedBytes) || 0;
    const current = pathMap.get(norm) || 0;
    if (bytes > current) {
      pathMap.set(norm, bytes);
    } else if (!pathMap.has(norm)) {
      pathMap.set(norm, bytes);
    }
  }

  // 2. 如果存在全桶根目录 ""，该目录已扫描并包含桶内所有子目录对象，直接作为桶总物理占用
  if (pathMap.has("")) {
    return pathMap.get("") || 0;
  }

  // 3. 按路径深度（长度）从短到长排序
  const sortedPaths = Array.from(pathMap.keys()).sort((a, b) => a.length - b.length);

  // 4. 贪心选取互不重叠包含的顶层前缀
  const topLevelPrefixes: string[] = [];
  let total = 0;

  for (const p of sortedPaths) {
    // 检查 p 是否已经被某个更顶层的前缀包含（例如 parent = "a/" 包含 p = "a/b/"）
    const isCovered = topLevelPrefixes.some((parent) => p.startsWith(parent));
    if (!isCovered) {
      topLevelPrefixes.push(p);
      total += pathMap.get(p) || 0;
    }
  }

  return total;
}
